import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { MapPin, Clock, Instagram, MessageCircle, Calendar, Coffee, Users, ArrowLeft, Navigation, X, ChevronLeft, ChevronRight } from "lucide-react";
import { getCafeImageUrls } from "@/lib/cafe-images";
import { MENU_SECTIONS, type MenuSection } from "@/components/cafe-menu-manager";

const MENU_BUCKET = "cafe-menu-images";

async function signMenuUrl(path: string): Promise<string | null> {
  const { data } = await supabase.storage.from(MENU_BUCKET).createSignedUrl(path, 60 * 60 * 24 * 365);
  return data?.signedUrl ?? null;
}

type MenuPhoto = { id: string; section: MenuSection; image_path: string; url: string | null };

export const Route = createFileRoute("/cafe/$slug")({
  head: ({ params }) => ({
    meta: [
      { title: `Café ${params.slug} — Brulo` },
      { name: "description", content: "Café Brulo — punto de encuentro para atletas." },
      { property: "og:title", content: `Café ${params.slug} — Brulo` },
      { property: "og:description", content: "Café Brulo — punto de encuentro para atletas." },
    ],
  }),
  component: CafePage,
});

type Cafe = {
  id: string;
  name: string;
  location: string | null;
  city: string | null;
  estado: string | null;
  hours: string | null;
  opens_at: string | null;
  closes_at: string | null;
  description: string | null;
  member_discount: string | null;
  phone: string | null;
  instagram: string | null;
  google_maps_url: string | null;
  latitude: number | null;
  longitude: number | null;
  logo_url: string | null;
  banner_url: string | null;
  slug: string;
};

// (menu items table replaced by cafe_menu_photos)

type EventRow = {
  id: string;
  name: string;
  event_date: string;
  event_time: string;
  club_id: string | null;
  clubName?: string | null;
  clubSlug?: string | null;
};

type ClubVisitor = { id: string; name: string; slug: string; visits: number };

function formatTime(t: string | null) {
  if (!t) return null;
  return t.slice(0, 5);
}

function CafePage() {
  const { slug } = Route.useParams();
  const [cafe, setCafe] = useState<Cafe | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [bannerUrl, setBannerUrl] = useState<string | null>(null);
  const [menu, setMenu] = useState<MenuPhoto[]>([]);
  const [lightbox, setLightbox] = useState<{ items: MenuPhoto[]; index: number } | null>(null);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [clubs, setClubs] = useState<ClubVisitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data: c } = await supabase
        .from("cafes")
        .select("id,name,location,city,estado,hours,opens_at,closes_at,description,member_discount,phone,instagram,google_maps_url,latitude,longitude,logo_url,banner_url,slug")
        .eq("slug", slug)
        .maybeSingle<Cafe>();
      if (!c) { setNotFound(true); setLoading(false); return; }
      setCafe(c);
      const [logo, banner] = await getCafeImageUrls([c.logo_url, c.banner_url]);
      setLogoUrl(logo);
      setBannerUrl(banner);

      // Menu photos
      const { data: mi } = await supabase
        .from("cafe_menu_photos" as never)
        .select("id,section,image_path,sort_order")
        .eq("cafe_id", c.id)
        .order("section")
        .order("sort_order");
      const mList = ((mi ?? []) as unknown as { id: string; section: MenuSection; image_path: string }[]);
      const urls = await Promise.all(mList.map((m) => signMenuUrl(m.image_path)));
      setMenu(mList.map((m, i) => ({ id: m.id, section: m.section, image_path: m.image_path, url: urls[i] })));

      // Events upcoming
      const today = new Date().toISOString().slice(0, 10);
      const { data: evs } = await supabase
        .from("events")
        .select("id,name,event_date,event_time,club_id")
        .eq("cafe_id", c.id)
        .eq("cancelado", false)
        .gte("event_date", today)
        .order("event_date", { ascending: true });
      const evList = (evs ?? []) as EventRow[];

      // Clubs - from all (past+future) events for "visitors", and resolve names for events
      const { data: allEvs } = await supabase
        .from("events")
        .select("club_id")
        .eq("cafe_id", c.id);
      const allClubIds = Array.from(new Set([
        ...evList.map((e) => e.club_id).filter((x): x is string => !!x),
        ...((allEvs ?? []) as { club_id: string | null }[]).map((e) => e.club_id).filter((x): x is string => !!x),
      ]));
      let clubMap = new Map<string, { id: string; name: string; slug: string }>();
      if (allClubIds.length) {
        const { data: cl } = await supabase
          .from("clubs")
          .select("id,name,slug")
          .in("id", allClubIds);
        (cl ?? []).forEach((club: { id: string; name: string; slug: string }) => clubMap.set(club.id, club));
      }
      setEvents(evList.map((e) => ({
        ...e,
        clubName: e.club_id ? clubMap.get(e.club_id)?.name ?? null : null,
        clubSlug: e.club_id ? clubMap.get(e.club_id)?.slug ?? null : null,
      })));

      const visitsFreq = new Map<string, number>();
      ((allEvs ?? []) as { club_id: string | null }[]).forEach((e) => {
        if (e.club_id) visitsFreq.set(e.club_id, (visitsFreq.get(e.club_id) ?? 0) + 1);
      });
      const visitors: ClubVisitor[] = Array.from(visitsFreq.entries())
        .map(([id, visits]) => {
          const club = clubMap.get(id);
          if (!club) return null;
          return { id, name: club.name, slug: club.slug, visits };
        })
        .filter((x): x is ClubVisitor => x !== null)
        .sort((a, b) => b.visits - a.visits);
      setClubs(visitors);

      setLoading(false);
    })();
  }, [slug]);

  if (loading) {
    return <div className="mx-auto max-w-4xl px-4 py-20 text-muted-foreground">Cargando...</div>;
  }
  if (notFound || !cafe) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-20">
        <h1 className="font-display text-3xl">Café no encontrado</h1>
        <Link to="/" className="text-primary hover:underline mt-4 inline-block">Volver al inicio</Link>
      </div>
    );
  }

  const hoursText = cafe.hours || (cafe.opens_at && cafe.closes_at ? `${formatTime(cafe.opens_at)} - ${formatTime(cafe.closes_at)}` : null);
  const waNumber = cafe.phone?.replace(/[^\d]/g, "") ?? "";
  const mapsUrl = cafe.google_maps_url
    ?? (cafe.latitude && cafe.longitude
      ? `https://www.google.com/maps/search/?api=1&query=${cafe.latitude},${cafe.longitude}`
      : cafe.location
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(cafe.location)}`
        : null);

  return (
    <div className="pb-16">
      <div className="mx-auto max-w-5xl px-4 md:px-6 pt-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Inicio
        </Link>
      </div>

      {/* Banner + logo */}
      <div className="relative mt-4 mx-auto max-w-5xl px-4 md:px-6">
        <div className="relative aspect-[3/1] sm:aspect-[4/1] rounded-xl overflow-hidden border border-border bg-gradient-to-br from-muted/60 to-muted/20">
          {bannerUrl ? (
            <img src={bannerUrl} alt={`Banner de ${cafe.name}`} className="absolute inset-0 w-full h-full object-cover" />
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
        </div>
        <div className="absolute left-8 md:left-12 -bottom-12 md:-bottom-14">
          <div className="w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden border-4 border-background bg-muted shadow-xl">
            {logoUrl ? (
              <img src={logoUrl} alt={`Logo de ${cafe.name}`} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-2xl font-display text-muted-foreground">
                {cafe.name.charAt(0)}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-4 md:px-6 pt-20 md:pt-24 space-y-10">
        {/* Header */}
        <header className="space-y-5">
          <p className="text-xs tracking-[0.3em] uppercase text-primary">café brulo</p>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl leading-tight">{cafe.name}</h1>
          {cafe.description && (
            <p className="text-base md:text-lg text-foreground/90 leading-relaxed max-w-2xl whitespace-pre-line">{cafe.description}</p>
          )}
          <div className="flex flex-wrap gap-2">
            {mapsUrl && (
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90"
              >
                <Navigation className="w-4 h-4" /> Cómo llegar
              </a>
            )}
            {waNumber && (
              <a
                href={`https://wa.me/${waNumber}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-green-600 text-white text-sm font-semibold hover:opacity-90"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            )}
            {cafe.instagram && (
              <a
                href={`https://instagram.com/${cafe.instagram.replace(/^@/, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-border text-sm font-semibold hover:bg-muted/50"
              >
                <Instagram className="w-4 h-4" /> @{cafe.instagram.replace(/^@/, "")}
              </a>
            )}
          </div>
        </header>

        {/* Info */}
        <section className="grid sm:grid-cols-2 gap-4">
          {(cafe.location || cafe.city) && (
            <div className="rounded-lg border border-border bg-card p-5">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Dirección</p>
              <p className="text-sm">{cafe.location}</p>
              {(cafe.city || cafe.estado) && (
                <p className="text-xs text-muted-foreground mt-1">{[cafe.city, cafe.estado].filter(Boolean).join(", ")}</p>
              )}
            </div>
          )}
          {hoursText && (
            <div className="rounded-lg border border-border bg-card p-5">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Horario</p>
              <p className="text-sm">{hoursText}</p>
            </div>
          )}
        </section>

        {/* Descuento Brulo */}
        {cafe.member_discount && (
          <section className="rounded-xl border-2 border-primary/40 bg-primary/10 p-6 flex items-start gap-4">
            <div className="text-4xl">🏃</div>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-primary font-semibold mb-1">Descuento Brulo</p>
              <p className="text-lg font-semibold text-foreground">Miembros Brulo: {cafe.member_discount}</p>
            </div>
          </section>
        )}

        {/* Menu */}
        {menu.length > 0 && (
          <section>
            <h2 className="font-display text-2xl mb-5 flex items-center gap-2">
              <Coffee className="w-5 h-5 text-primary" /> Menú
            </h2>
            <div className="space-y-8">
              {MENU_SECTIONS.map((sec) => {
                const items = menu.filter((m) => m.section === sec.key);
                if (items.length === 0) return null;
                return (
                  <div key={sec.key}>
                    <h3 className="font-display text-xl mb-3 flex items-center gap-2">
                      <span>{sec.emoji}</span> {sec.label}
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {items.map((it, idx) => (
                        <button
                          key={it.id}
                          type="button"
                          onClick={() => setLightbox({ items, index: idx })}
                          className="group relative rounded-lg border border-border overflow-hidden bg-card aspect-[3/4] focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          {it.url ? (
                            <img
                              src={it.url}
                              alt={`${sec.label} - foto ${idx + 1}`}
                              loading="lazy"
                              className="absolute inset-0 w-full h-full object-cover transition group-hover:scale-[1.03]"
                            />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center text-muted-foreground"><Coffee className="w-8 h-8" /></div>
                          )}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition" />
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Upcoming events */}
        <section>
          <h2 className="font-display text-2xl mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" /> Próximos eventos aquí
          </h2>
          {events.length === 0 ? (
            <p className="text-muted-foreground text-sm">No hay eventos próximos en este café.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {events.map((ev) => {
                const dateStr = new Date(`${ev.event_date}T00:00:00`).toLocaleDateString("es-MX", {
                  weekday: "short", day: "numeric", month: "short",
                });
                return (
                  <Link
                    key={ev.id}
                    to="/eventos/$eventId"
                    params={{ eventId: ev.id }}
                    className="block rounded-lg border bg-background/40 p-4 hover:bg-background/60 transition border-border hover:border-primary/40"
                  >
                    {ev.clubName && (
                      <p className="text-xs uppercase tracking-wider text-primary mb-1">{ev.clubName}</p>
                    )}
                    <h3 className="font-display text-lg leading-snug mb-2">{ev.name}</h3>
                    <p className="text-sm text-muted-foreground capitalize">{dateStr} · {ev.event_time?.slice(0, 5)}</p>
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        {/* Visiting clubs */}
        {clubs.length > 0 && (
          <section>
            <h2 className="font-display text-2xl mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" /> Clubes que nos visitan
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {clubs.map((c) => (
                <Link
                  key={c.id}
                  to="/club/$slug"
                  params={{ slug: c.slug }}
                  className="rounded-lg border border-border bg-card p-5 hover:border-primary/40 transition block"
                >
                  <h3 className="font-display text-lg">{c.name}</h3>
                  <p className="text-xs text-muted-foreground mt-1">{c.visits} {c.visits === 1 ? "visita" : "visitas"}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      {lightbox && (
        <Lightbox
          items={lightbox.items}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
          onChange={(i) => setLightbox({ items: lightbox.items, index: i })}
        />
      )}
    </div>
  );
}

function Lightbox({
  items,
  index,
  onClose,
  onChange,
}: {
  items: MenuPhoto[];
  index: number;
  onClose: () => void;
  onChange: (i: number) => void;
}) {
  const total = items.length;
  const prev = useCallback(() => onChange((index - 1 + total) % total), [index, total, onChange]);
  const next = useCallback(() => onChange((index + 1) % total), [index, total, onChange]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, prev, next]);

  const current = items[index];
  if (!current) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center"
        aria-label="Cerrar"
      >
        <X className="w-5 h-5" />
      </button>
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center"
            aria-label="Anterior"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center"
            aria-label="Siguiente"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}
      {current.url && (
        <img
          src={current.url}
          alt={`Menú ${index + 1} de ${total}`}
          onClick={(e) => e.stopPropagation()}
          className="max-w-[92vw] max-h-[88vh] object-contain rounded-md shadow-2xl"
        />
      )}
      {total > 1 && (
        <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm">
          {index + 1} / {total}
        </p>
      )}
    </div>
  );
}
